import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { usePowerSync, useQuery } from "@powersync/react";
import { PMButton, PMIcon } from "@/ds/vendor";
import { useApp } from "@/app/AppContext";
import { askLocal, type Answer, type Citation } from "@/data/ask";
import { levelDef } from "@/lib/levels";
import { Character } from "@/ui/Character";
import { relTime, shortDate } from "@/ui/format";
import "./ask.css";

// Ask Propel (core-features §5): tag a location with @, ask in plain language,
// get an answer built from field logs with clickable source citations.
interface Turn { id: string; q: string; at: { id: string; label: string } | null; a: Answer | null }
const RECENT = "pm.ask.recent";
const KIND: Record<Citation["kind"], { label: string; emoji: string }> = {
  summary: { label: "Visit summary", emoji: "📋" }, voice: { label: "Voice note", emoji: "🎙️" }, note: { label: "Note", emoji: "📝" },
  photo: { label: "Photo text", emoji: "📷" }, report: { label: "Signed report", emoji: "📄" }, metric: { label: "Form value", emoji: "📈" },
};

export function AskPage() {
  const { teamId } = useParams({ from: "/t/$teamId/ask" });
  const search = useSearch({ from: "/t/$teamId/ask" });
  const nav = useNavigate();
  const db = usePowerSync();
  const { hierarchy, mode } = useApp();
  const locs = useQuery<{ id: string; label: string; level: string }>("SELECT mf.id, mf.label, mf.level FROM map_features mf JOIN maps m ON m.id = mf.map_id WHERE m.team_id = ? ORDER BY mf.level, mf.label", [teamId]).data;
  const [text, setText] = useState("");
  const [at, setAt] = useState<{ id: string; label: string } | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [mention, setMention] = useState<string | null>(null);
  const [sel, setSel] = useState(0);
  const input = useRef<HTMLTextAreaElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const recent = useMemo<string[]>(() => { try { return JSON.parse(localStorage.getItem(RECENT) || "[]"); } catch { return []; } }, [turns.length]);

  const matches = mention !== null ? locs.filter((l) => l.label.toLowerCase().includes(mention.toLowerCase())).slice(0, 7) : [];

  const run = async (q: string, where: { id: string; label: string } | null) => {
    if (!q.trim()) return;
    const id = crypto.randomUUID();
    setTurns((t) => [...t, { id, q, at: where, a: null }]);
    setText("");
    try { localStorage.setItem(RECENT, JSON.stringify([q, ...recent.filter((r) => r !== q)].slice(0, 6))); } catch { /* ignore */ }
    const a = await askLocal(db, teamId, q, where?.id);
    setTurns((t) => t.map((x) => (x.id === id ? { ...x, a } : x)));
  };

  // Deep link from ⌘K or a location page: /ask?at=<id>&q=...
  useEffect(() => {
    if (!search.at && !search.q) return;
    const l = locs.find((x) => x.id === search.at);
    if (search.at && !l) return; // wait for locations to load
    const where = l ? { id: l.id, label: l.label } : null;
    setAt(where);
    if (search.q) void run(search.q, where);
    nav({ to: ".", search: {}, replace: true } as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.at, search.q, locs.length]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [turns]);

  const onChange = (v: string) => {
    setText(v);
    const m = v.match(/@([^@\n]*)$/);
    setMention(m ? m[1] : null);
    setSel(0);
  };
  const pickMention = (l: { id: string; label: string }) => {
    setAt(l);
    setText((t) => t.replace(/@([^@\n]*)$/, "").trimStart());
    setMention(null);
    input.current?.focus();
  };
  const onKey = (e: React.KeyboardEvent) => {
    if (mention !== null && matches.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, matches.length - 1)); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pickMention(matches[sel]); return; }
      if (e.key === "Escape") { setMention(null); return; }
    }
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void run(text, at); }
    if (e.key === "Backspace" && !text && at) setAt(null);
  };

  const chips = at
    ? ["Summarize the last 3 visits", "How has stand establishment gone over the past two weeks?", "Any pest or disease pressure this month?", "What follow-ups came up recently?", "Check yield metrics this season"]
    : ["Where is disease pressure showing up this month?", "Summarize the last 5 visits", "Which fields had nitrogen issues?", "What did we find about water or drainage?"];

  const openCite = (c: Citation) => nav({ to: "/t/$teamId/visits/$visitId", params: { teamId, visitId: c.visitId }, search: { tab: c.kind === "metric" ? "forms" : c.obsId ? "observations" : "report" } } as any);

  return (
    <div className={`ask ${turns.length ? "ask--thread" : ""}`}>
      {!turns.length && (
        <div className="ask-hero">
          <span className="pm-chip-section">ASK PROPEL</span>
          <h1>Ask the field notes</h1>
          <p className="muted">Tag a {hierarchy.levels[0].label.toLowerCase()} or {hierarchy.levels.at(-1)!.label.toLowerCase()} with <kbd>@</kbd>, then ask in plain language. Answers cite the exact visit, voice note or report they came from.</p>
        </div>
      )}

      <div className="ask-thread">
        {turns.map((t) => (
          <div className="ask-turn" key={t.id}>
            <div className="ask-q"><span>{t.at && <span className="ask-tag">@{t.at.label}</span>} {t.q}</span></div>
            <div className="ask-a">
              <div className="ask-a-head"><span className="ask-mark">P</span><strong>Propel</strong><span className="hint">{t.a ? `searched ${t.a.scope.visits} visits` : "searching field notes…"}</span></div>
              {!t.a ? <div className="ask-typing" aria-label="Searching"><i /><i /><i /></div> : (
                <>
                  <p className="ask-headline">{t.a.headline}</p>
                  {t.a.trend && (
                    <div className="ask-trend">
                      <span className="stat-lbl">{t.a.trend.metric.toUpperCase()}</span>
                      <strong>{t.a.trend.from} → {t.a.trend.to} <small>{t.a.trend.unit}</small></strong>
                      <span className={`ask-delta ${t.a.trend.to >= t.a.trend.from ? "up" : "down"}`}>{t.a.trend.to >= t.a.trend.from ? "▲" : "▼"} {Math.abs(Math.round(((t.a.trend.to - t.a.trend.from) / (t.a.trend.from || 1)) * 100))}% over {t.a.trend.n} readings</span>
                      <button className="ask-sup" onClick={() => openCite(t.a!.citations[t.a!.trend!.cite - 1])}>{t.a.trend.cite}</button>
                    </div>
                  )}
                  {t.a.points.length > 0 && (
                    <ul className="ask-points">
                      {t.a.points.map((p, i) => <li key={i}>{p.text} {p.cites.map((c) => <button key={c} className="ask-sup" title={`${KIND[t.a!.citations[c - 1].kind].label} · ${t.a!.citations[c - 1].location} · ${shortDate(t.a!.citations[c - 1].date)}`} onClick={() => openCite(t.a!.citations[c - 1])}>{c}</button>)}</li>)}
                    </ul>
                  )}
                  {t.a.citations.length > 0 && (
                    <div className="ask-sources">
                      <div className="eyebrow" style={{ margin: "0 0 8px" }}>Sources <span className="count-pill">{t.a.citations.length}</span></div>
                      <div className="ask-source-grid">
                        {t.a.citations.map((c) => (
                          <button key={c.n} className="ask-source" onClick={() => openCite(c)}>
                            <span className="ask-source-n">{c.n}</span>
                            <span className="list-main"><span className="ask-source-t">{KIND[c.kind].emoji} {KIND[c.kind].label} · {c.location}</span><span className="hint">{shortDate(c.date)} · {relTime(c.date)}</span></span>
                            <PMIcon name="arrow-right" size={16} />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {!t.a.points.length && !t.a.trend && <div className="ask-empty"><Character name="cow" size={70} title="Nothing in the notes" line="Try a wider time range, or tag a different location." /></div>}
                </>
              )}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <div className="ask-composer-wrap">
        <div className="ask-chips">
          {chips.map((c) => <button key={c} className="ask-chip" onClick={() => run(c, at)}>{c}</button>)}
        </div>
        <div className="ask-composer">
          {mention !== null && matches.length > 0 && (
            <div className="ask-mentions" role="listbox">
              {matches.map((l, i) => { const d = levelDef(l.level, hierarchy.key); return (
                <button key={l.id} role="option" aria-selected={i === sel} className={i === sel ? "is-sel" : ""} onMouseEnter={() => setSel(i)} onClick={() => pickMention(l)}>
                  <span>{d.emoji}</span><span className="list-main">{l.label}</span><span className="hint">{d.label}</span>
                </button>
              ); })}
            </div>
          )}
          {at && <button className="ask-tag ask-tag--chip" onClick={() => setAt(null)} aria-label={`Remove ${at.label}`}>@{at.label} ✕</button>}
          <textarea ref={input} rows={1} value={text} onChange={(e) => onChange(e.target.value)} onKeyDown={onKey} placeholder={at ? `Ask about ${at.label}` : `Ask anything, or type @ to tag a ${hierarchy.levels[0].label.toLowerCase()}`} aria-label="Ask Propel" autoFocus />
          <PMButton size="sm" disabled={!text.trim()} onClick={() => run(text, at)}>Ask</PMButton>
        </div>
        <div className="ask-foot hint">
          {recent.length > 0 && !turns.length ? <>Recent: {recent.slice(0, 3).map((r) => <button key={r} className="linkbtn" onClick={() => run(r, at)}>{r}</button>)}</> : null}
          <span className="spacer" />
          <span>Answers come from field notes synced to this device{mode === "demo" ? " (demo team)" : ""}.</span>
        </div>
      </div>
    </div>
  );
}
